export interface ProductPricingDetails {
  productNumber: string;
  description: string;
  // Charged amount in US cents; drives all on-screen totals and email money math.
  price: number;
  // Optional promo metadata: when present, `price` is the discounted charge and `listPrice`
  // is the original amount shown struck through, labelled by `promoLabel`.
  listPrice?: number;
  promoLabel?: string;
}

// Pricing keyed by product name, as persisted onto productDetails[product].pricing at submit time.
export type ProductPricingMap = Record<string, { pricing: ProductPricingDetails[] }>;

export enum products {
  C360_BASIC = 'C360_BASIC',
  C360_ADVANCED = 'C360_ADVANCED',
  C360_PRO = 'C360_PRO',
  E360A = 'E360A',
  E360P = 'E360P',
  MX4 = 'MX4',
  IFIT = 'IFIT',
  FACILITY360 = 'FACILITY360',
  AM_VISION = 'AM_VISION',
}

// ALL PRICES IN US CENTS
export const productPricing = {
  'MDP0022-OS': { description: 'Community 360 Basic Implementation Fee', price: 49900 },
  'MDP0023-OS': { description: 'Community 360 Basic 1YR - First Facility', price: 142800 },
  'MDP0024-OS': { description: 'Community 360 Basic 2YR - First Facility', price: 285600 },
  'MDP0025-OS': { description: 'Community 360 Basic 3YR - First Facility', price: 428400 },
  'MDP0027-OS': { description: 'Community 360 Basic 4YR - First Facility', price: 571200 },
  'MDP0028-OS': { description: 'Community 360 Basic 5YR - First Facility', price: 714000 },
  'MDP0029-OS': { description: 'Community 360 Basic 1YR - Additional Facility', price: 118800 },
  'MDP0030-OS': { description: 'Community 360 Basic 2YR - Additional Facility', price: 237600 },
  'MDP0031-OS': { description: 'Community 360 Basic 3YR - Additional Facility', price: 356400 },
  'MDP0032-OS': { description: 'Community 360 Basic 4YR - Additional Facility', price: 475200 },
  'MDP0033-OS': { description: 'Community 360 Basic 5YR - Additional Facility', price: 594000 },
  'MDP0034-OS': { description: 'Community 360 ADV Implementation Fee', price: 49900 },
  'MDP0035-OS': { description: 'Community 360 ADV 1YR - First Facility', price: 262800 },
  'MDP0036-OS': { description: 'Community 360 ADV 2YR - First Facility', price: 525600 },
  'MDP0037-OS': { description: 'Community 360 ADV 3YR - First Facility', price: 788400 },
  'MDP0038-OS': { description: 'Community 360 ADV 4YR - First Facility', price: 1051200 },
  'MDP0039-OS': { description: 'Community 360 ADV 5YR - First Facility', price: 1314000 },
  'MDP0040-OS': { description: 'Community 360 ADV 1YR - Additional Facility', price: 142800 },
  'MDP0041-OS': { description: 'Community 360 ADV 2YR - Additional Facility', price: 285600 },
  'MDP0042-OS': { description: 'Community 360 ADV 3YR - Additional Facility', price: 428400 },
  'MDP0043-OS': { description: 'Community 360 ADV 4YR - Additional Facility', price: 571200 },
  'MDP0044-OS': { description: 'Community 360 ADV 5YR - Additional Facility', price: 714000 },
  'MDP0045-OS': { description: 'Community 360 PRO Implementation Fee', price: 99900 },
  'MDP0046-OS': { description: 'Community 360 PRO 1YR - First Facility', price: 382800 },
  'MDP0047-OS': { description: 'Community 360 PRO 2YR - First Facility', price: 765600 },
  'MDP0048-OS': { description: 'Community 360 PRO 3YR - First Facility', price: 1148400 },
  'MDP0049-OS': { description: 'Community 360 PRO 4YR - First Facility', price: 1531200 },
  'MDP0050-OS': { description: 'Community 360 PRO 5YR - First Facility', price: 1914000 },
  'MDP0051-OS': { description: 'Community 360 PRO 1YR - Additional Facility', price: 154800 },
  'MDP0052-OS': { description: 'Community 360 PRO 2YR - Additional Facility', price: 309600 },
  'MDP0053-OS': { description: 'Community 360 PRO 3YR - Additional Facility', price: 464400 },
  'MDP0054-OS': { description: 'Community 360 PRO 4YR - Additional Facility', price: 619200 },
  'MDP0055-OS': { description: 'Community 360 PRO 5YR - Additional Facility', price: 774000 },

  'MDP0009-OS': { description: 'MX4 APP FEE-1YR', price: 54000 },

  'MDP0018-OS': { description: 'Engage 360 ADV 1st year and maintenance fee', price: 240000 },
  'MDP0019-OS': { description: 'Engage 360 ADV annual maintenance fee', price: 39300 },
  'MDP0020-OS': { description: 'Engage 360 PRO 1st year and maintenance fee', price: 480000 },
  'MDP0021-OS': { description: 'Engage 360 PRO annual maintenance fee', price: 78600 },
};

export const facility360Pricing = {
  fac360TouchscreenCount: { description: 'Facility 360 for Touchscreen Consoles', price: 3058 },
  fac360LEDCount: { description: 'Facility 360 for LED Consoles', price: 2448 },
  fac360SensorCount: { description: 'Facility 360 Sensor', price: 8600 },
  fac360Gateway: { description: 'Facility 360 Gateway', price: 49900 },
};

// $49.00 lifetime per club, in US cents. Keep in sync with backend renderEmail.ts.
export const AM_VISION_PRICE_PER_CLUB = 4900;

export const formatCents = (cents: number) => {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDescription = (desc: string, count: number) =>
  count === 1 ? desc : `${desc} (${count})`;

class Com360BasicCalculator {
  getPricingInformation(years, facilityCount) {
    let firstFacilityProductCode, additionalFacProductCode;
    switch (years) {
      case 2:
        firstFacilityProductCode = 'MDP0024-OS';
        additionalFacProductCode = 'MDP0030-OS';
        break;
      case 3:
        firstFacilityProductCode = 'MDP0025-OS';
        additionalFacProductCode = 'MDP0031-OS';
        break;
      case 4:
        firstFacilityProductCode = 'MDP0027-OS';
        additionalFacProductCode = 'MDP0032-OS';
        break;
      case 5:
        firstFacilityProductCode = 'MDP0028-OS';
        additionalFacProductCode = 'MDP0033-OS';
        break;
      default:
        firstFacilityProductCode = 'MDP0023-OS';
        additionalFacProductCode = 'MDP0029-OS';
    }
    const prices = [
      {
        productNumber: 'MDP0022-OS',
        description: productPricing['MDP0022-OS'].description,
        price: productPricing['MDP0022-OS'].price,
      },
    ];
    prices.push({
      productNumber: firstFacilityProductCode,
      description: productPricing[firstFacilityProductCode].description,
      price: productPricing[firstFacilityProductCode].price,
    });
    if (facilityCount > 1) {
      prices.push({
        productNumber: additionalFacProductCode,
        description: formatDescription(
          productPricing[additionalFacProductCode].description,
          facilityCount - 1,
        ),
        price: productPricing[additionalFacProductCode].price * (facilityCount - 1),
      });
    }
    return prices;
  }
}
class Com360AdvancedCalculator {
  getPricingInformation(years, facilityCount) {
    let firstFacilityProductCode, additionalFacProductCode;
    switch (years) {
      case 2:
        firstFacilityProductCode = 'MDP0036-OS';
        additionalFacProductCode = 'MDP0041-OS';
        break;
      case 3:
        firstFacilityProductCode = 'MDP0037-OS';
        additionalFacProductCode = 'MDP0042-OS';
        break;
      case 4:
        firstFacilityProductCode = 'MDP0038-OS';
        additionalFacProductCode = 'MDP0043-OS';
        break;
      case 5:
        firstFacilityProductCode = 'MDP0039-OS';
        additionalFacProductCode = 'MDP0044-OS';
        break;
      default:
        firstFacilityProductCode = 'MDP0035-OS';
        additionalFacProductCode = 'MDP0040-OS';
    }
    const prices = [
      {
        productNumber: 'MDP0034-OS',
        description: productPricing['MDP0034-OS'].description,
        price: productPricing['MDP0034-OS'].price,
      },
    ];
    prices.push({
      productNumber: firstFacilityProductCode,
      description: productPricing[firstFacilityProductCode].description,
      price: productPricing[firstFacilityProductCode].price,
    });
    if (facilityCount > 1) {
      prices.push({
        productNumber: additionalFacProductCode,
        description: formatDescription(
          productPricing[additionalFacProductCode].description,
          facilityCount - 1,
        ),
        price: productPricing[additionalFacProductCode].price * (facilityCount - 1),
      });
    }
    return prices;
  }
}

class Com360ProCalculator {
  getPricingInformation(years, facilityCount) {
    let firstFacilityProductCode, additionalFacProductCode;
    switch (years) {
      case 2:
        firstFacilityProductCode = 'MDP0047-OS';
        additionalFacProductCode = 'MDP0052-OS';
        break;
      case 3:
        firstFacilityProductCode = 'MDP0048-OS';
        additionalFacProductCode = 'MDP0053-OS';
        break;
      case 4:
        firstFacilityProductCode = 'MDP0049-OS';
        additionalFacProductCode = 'MDP0054-OS';
        break;
      case 5:
        firstFacilityProductCode = 'MDP0050-OS';
        additionalFacProductCode = 'MDP0055-OS';
        break;
      default:
        firstFacilityProductCode = 'MDP0046-OS';
        additionalFacProductCode = 'MDP0051-OS';
    }
    const prices = [
      {
        productNumber: 'MDP0045-OS',
        description: productPricing['MDP0045-OS'].description,
        price: productPricing['MDP0045-OS'].price,
      },
    ];
    prices.push({
      productNumber: firstFacilityProductCode,
      description: productPricing[firstFacilityProductCode].description,
      price: productPricing[firstFacilityProductCode].price,
    });
    if (facilityCount > 1) {
      prices.push({
        productNumber: additionalFacProductCode,
        description: formatDescription(
          productPricing[additionalFacProductCode].description,
          facilityCount - 1,
        ),
        price: productPricing[additionalFacProductCode].price * (facilityCount - 1),
      });
    }
    return prices;
  }
}

class Engage360Calculator {
  getPricingInformation(type, years, facilityCount) {
    let prices;
    if (type === 'E360A') {
      prices = [
        {
          productNumber: 'MDP0018-OS',
          description: formatDescription(productPricing['MDP0018-OS'].description, facilityCount),
          price: productPricing['MDP0018-OS'].price * facilityCount,
        },
      ];
    } else {
      prices = [
        {
          productNumber: 'MDP0020-OS',
          description: formatDescription(productPricing['MDP0020-OS'].description, facilityCount),
          price: productPricing['MDP0020-OS'].price * facilityCount,
        },
      ];
    }

    if (years > 1) {
      const multiplier = (years - 1) * facilityCount;
      if (type === 'E360A') {
        prices.push({
          productNumber: 'MDP0019-OS',
          description: formatDescription(productPricing['MDP0019-OS'].description, multiplier),
          price: productPricing['MDP0019-OS'].price * multiplier,
        });
      } else {
        prices.push({
          productNumber: 'MDP0021-OS',
          description: formatDescription(productPricing['MDP0021-OS'].description, multiplier),
          price: productPricing['MDP0021-OS'].price * multiplier,
        });
      }
    }
    return prices;
  }
}

class Mx4Calculator {
  getPricingInformation(years, facilityCount) {
    return [
      {
        productNumber: 'MDP0009-OS',
        description: formatDescription(
          productPricing['MDP0009-OS'].description,
          years * facilityCount,
        ),
        price: productPricing['MDP0009-OS'].price * years * facilityCount,
      },
    ];
  }
}

export const getPricingInformation = (product, years, facilityCount): ProductPricingDetails[] => {
  switch (product) {
    case products.C360_BASIC:
      return new Com360BasicCalculator().getPricingInformation(years, facilityCount);
    case products.C360_ADVANCED:
      return new Com360AdvancedCalculator().getPricingInformation(years, facilityCount);
    case products.C360_PRO:
      return new Com360ProCalculator().getPricingInformation(years, facilityCount);
    case products.E360A:
      return new Engage360Calculator().getPricingInformation('E360A', years, facilityCount);
    case products.E360P:
      return new Engage360Calculator().getPricingInformation('E360P', years, facilityCount);
    case products.MX4:
      return new Mx4Calculator().getPricingInformation(years, facilityCount);
    default:
      return [];
  }
};

export const getFacility360PricingInformation = (
  product,
  facilityData,
): ProductPricingDetails[] => {
  const totals = Object.values(facilityData).reduce(
    (memo, value) => {
      Object.keys(value).forEach(key => {
        typeof value[key] === 'boolean'
          ? (memo[key] += value[key] ? 1 : 0)
          : (memo[key] += +value[key]);
      });
      return memo;
    },
    {
      fac360TouchscreenCount: 0,
      fac360LEDCount: 0,
      fac360SensorCount: 0,
      fac360Gateway: 0,
    },
  );
  return Object.keys(totals)
    .filter(field => +totals[field] > 0)
    .map(field => {
      const count = +totals[field];
      return {
        productNumber: `(${count})`,
        description: facility360Pricing[field].description,
        price: count * facility360Pricing[field].price,
      };
    });
};

export const getAmVisionPricingInformation = (
  facilityData,
  facilityCount,
): ProductPricingDetails[] => {
  // Vision AM is $49 per club. The console counts don't change the amount, but at least one
  // console (Touchscreen or LED) must be entered before any pricing applies.
  if (!facilityData || !facilityCount || facilityCount < 1) {
    return [];
  }
  const totalConsoles = Object.values(facilityData).reduce<number>((sum, facility: any) => {
    if (!facility || typeof facility !== 'object') {
      return sum;
    }
    const counts = facility as Record<string, number | string>;
    return sum + (+counts.amVisionTouchscreenCount || 0) + (+counts.amVisionLEDCount || 0);
  }, 0);
  if (totalConsoles < 1) {
    return [];
  }
  // Limited-time promo: charge $0 while still showing the $49/club list price struck through.
  // To revert, set `price` back to `facilityCount * AM_VISION_PRICE_PER_CLUB` and drop the promo keys.
  return [
    {
      productNumber: `(${facilityCount})`,
      description: 'Asset Management for Vision',
      price: 0,
      listPrice: facilityCount * AM_VISION_PRICE_PER_CLUB,
      promoLabel: 'Limited-Time Free',
    },
  ];
};

interface GetProductPricingParams {
  productName: string;
  product: string;
  years: number;
  facilityCount: number;
  details: unknown;
}

// Single source of truth for per-product pricing routing, shared by the order-summary display
// (ProductPricing) and the submission builder (OrderSummary) so the two cannot drift apart.
export const getProductPricing = ({
  productName,
  product,
  years,
  facilityCount,
  details,
}: GetProductPricingParams): ProductPricingDetails[] => {
  switch (productName) {
    case 'AM_VISION':
      return getAmVisionPricingInformation(details, facilityCount);
    case 'FACILITY360':
      return getFacility360PricingInformation(product, details);
    default:
      return getPricingInformation(product, years, facilityCount);
  }
};

export const getInformationFromValues = (productName, subformValues) => {
  let product;
  let years = 0;
  if (
    productName === 'COM360' &&
    subformValues.c360Type &&
    subformValues.c360Type === 'c360_basic_app'
  ) {
    product = products.C360_BASIC;
  } else if (
    productName === 'COM360' &&
    subformValues.c360Type &&
    subformValues.c360Type === 'c360_advanced_app'
  ) {
    product = products.C360_ADVANCED;
  } else if (
    productName === 'COM360' &&
    subformValues.c360Type &&
    subformValues.c360Type === 'c360_pro_app'
  ) {
    product = products.C360_PRO;
  } else if (
    productName === 'ENG360' &&
    subformValues.e360Type &&
    subformValues.e360Type === 'engage_360_advanced'
  ) {
    product = products.E360A;
  } else if (
    productName === 'ENG360' &&
    subformValues.e360Type &&
    subformValues.e360Type === 'engage_360_pro'
  ) {
    product = products.E360P;
  } else if (productName === 'MX4') {
    product = products.MX4;
  } else if (productName === 'iFIT') {
    product = products.IFIT;
  } else if (productName === 'FACILITY360') {
    product = products.FACILITY360;
  } else if (productName === 'AM_VISION') {
    product = products.AM_VISION;
  }

  if (subformValues.orderDuration && subformValues.orderDuration.length) {
    years = Math.max(Number(subformValues.orderDuration[0]), 0);
  }
  return { years, product };
};
